/**
 * One event of the document as the `<note>` elements MusicXML wants for it.
 *
 * A chord is one event here and several `<note>` elements there, the second and later ones
 * carrying `<chord/>`, so this returns a list rather than a node. The order of the children
 * inside a `<note>` is fixed by the format's own content model and is not a matter of
 * taste: readers that validate reject a file that puts `<stem>` before `<accidental>`.
 * That order is the order of the code below, and is the reason this is a long function
 * rather than a set of small ones that could be called in any sequence.
 */

import { ticksOf } from "../document/duration";
import { drumOf } from "../document/percussion";
import type { DrumNote, PercussionKit } from "../document/percussion";
import type { NotePitch } from "../document/pitch";
import type { BeamState, Lyric, MusicEvent, NoteEvent, Offset, RestEvent } from "../document/types";
import { el, leaf } from "../xml/write";
import type { XmlNode } from "../xml/write";
import { toDivisions } from "./common";
import { accidentalName } from "./export-accidentals";
import { drumInstrumentId, noteheadNode, unpitchedNode } from "./export-drums";
import type { BeamMark } from "./export-beams";
import { marksFor, notationsNode } from "./export-notations";
import type { GlissMark, SlurMark, TupletMark } from "./export-notations";

export type { GlissMark, SlurMark, TupletMark } from "./export-notations";

/** Everything about an event that only the surrounding bar knows. */
export interface NoteContext {
  readonly divisions: number;
  /**
   * Ticks the bar holds, written as the duration of a whole-measure rest.
   *
   * The document spells that rest `whole` whatever the metre, because it means "this bar
   * is silent" rather than "a semibreve happens here". MusicXML has no such notion and
   * counts its `<duration>` against the bar like any other, so a bar of 3/4 exported as
   * four quarters of silence reads back as an overfull bar everywhere.
   */
  readonly measureTicks: number;
  /** MusicXML voice number, 1-based and unique within the part. */
  readonly voice: number;
  /** MusicXML staff number, 1-based, or `null` when the part has a single staff. */
  readonly staff: number | null;
  /**
   * The number of each of the part's own staves, by staff index, for a note drawn on a
   * staff that is not its voice's.
   *
   * Not `index + 1`: a part carrying an ossia writes a staff for it, and the staves under
   * that one are numbered past it. Empty where there is nowhere to reach -- a part on one
   * staff, or a voice that is itself an ossia.
   */
  readonly staves: readonly number[];
  readonly slurs: readonly SlurMark[];
  /** Glissando ends landing here, written into the same `<notations>` a slur is. */
  readonly glissandos: readonly GlissMark[];
  readonly tuplet: TupletMark;
  /** What each head of this event prints in front of it, by position in the chord. */
  readonly accidentals: readonly (string | null)[];
  /**
   * Beams worked out for this event, used only where it left its own beaming open.
   *
   * Empty when the caller did not ask for beams to be derived, which is the default and is
   * what keeps the file a transcript of the document rather than of an engraving.
   */
  readonly beams: readonly BeamMark[];
  /**
   * The event's own id, written as the `id` attribute, or `null` to leave it out.
   *
   * A renderer that keeps the attribute -- Verovio does -- puts it on the SVG element it
   * draws for the note, which turns hit-testing a click into a lookup rather than a search
   * through coordinates. Chord members after the first take the id with a `.1`, `.2` suffix,
   * because an XML id has to be unique and the notes of one chord are one event here.
   */
  readonly id: string | null;
  /** The drums this part is written with, or `null` for a part that has pitches. */
  readonly kit: PercussionKit | null;
  /** The part's own id in the file, which the instrument a drum names is written inside. */
  readonly partId: string;
}

/**
 * The `<accidental>` of one head: the one the document asked for, or the one worked out.
 *
 * A head left on `auto` takes what the part's plan decided for it, which is `null` where
 * the note needs nothing drawn in front of it. Nothing is left for the reader to decide,
 * for the reasons in `export-accidentals.ts`.
 */
function accidentalNode(pitch: NotePitch, decided: string | null): XmlNode | null {
  if (pitch.accidental === "auto") {
    return decided === null ? null : leaf("accidental", decided);
  }
  if (pitch.accidental === "none") {
    const implied = accidentalName(pitch.alter);
    return implied === null ? null : leaf("accidental", implied, { "print-object": "no" });
  }
  const named = pitch.accidental === "double-flat" ? "flat-flat" : pitch.accidental;
  return leaf("accidental", named);
}

/** Which drum of the kit this head is, as the reference the part list can be read by. */
function instrumentNode(context: NoteContext, drum: DrumNote | null): XmlNode | null {
  if (drum === null || context.kit === null) return null;
  const id = drumInstrumentId(context.partId, context.kit, drum.name);
  return id === null ? null : el("instrument", { id });
}

function timeModification(event: MusicEvent): XmlNode | null {
  const tuplet = event.duration.tuplet;
  if (tuplet === null) return null;
  return el("time-modification", {}, [
    leaf("actual-notes", tuplet.actual),
    leaf("normal-notes", tuplet.normal),
  ]);
}

/**
 * The `<beam>` elements of one note: its own, or the ones worked out for it.
 *
 * A note beamed by hand is written as it stands and the derived marks are ignored, so that
 * turning derivation on never overrules a decision already in the document. `none` writes
 * nothing, which is what MusicXML means by a note with no beam.
 */
function beamNodes(beam: BeamState, derived: readonly BeamMark[]): XmlNode[] {
  if (beam === "none") return [];
  if (beam !== "auto") return [leaf("beam", beam, { number: 1 })];
  return derived.map((mark) => leaf("beam", mark.value, { number: mark.number }));
}

/**
 * The `<lyric>` elements of one note.
 *
 * Sung text hangs off the first note of a chord only: a singer has one line of words for
 * what the piece treats as one attack, and repeating the syllable on every chord member
 * writes it under the staff once per note head.
 */
function lyricNodes(lyrics: readonly Lyric[]): XmlNode[] {
  return lyrics.map((lyric) =>
    el("lyric", { number: lyric.verse }, [
      leaf("syllabic", lyric.syllabic),
      leaf("text", lyric.text),
      lyric.extend ? el("extend") : null,
    ]),
  );
}

/**
 * Which staff of the part this note is drawn on, 1-based, or `null` for a part with one.
 *
 * The voice's staff is the answer unless the note was sent to another one, which is how
 * MusicXML writes a cross-staff note too: the `<voice>` stays put and the `<staff>` moves,
 * so the beam is still the voice's and the head is on the other staff.
 */
function staffOf(event: NoteEvent | RestEvent, context: NoteContext): number | null {
  if (context.staff === null) return null;
  if (event.staff === null) return context.staff;
  return context.staves[event.staff] ?? context.staff;
}

/**
 * The children every note shares, after its pitch or rest and its duration.
 *
 * `index` is which note of a chord this is and `count` how many there are. Stem direction,
 * beaming, articulations and sung text belong to the event rather than to one of a chord's
 * pitches, so they are written onto the first `<note>` only. A `<beam>` repeated on a chord
 * member is what makes Verovio refuse the whole chord, and a reader that took the last one
 * would still be reading the same value twice.
 */
function tail(
  event: NoteEvent | RestEvent,
  context: NoteContext,
  pitch: NotePitch | null,
  index: number,
  count: number,
): (XmlNode | null)[] {
  const first = index === 0;
  const isNote = event.kind === "note";
  const beam = isNote && first ? event.beam : "auto";
  const lyrics = isNote && first ? event.lyrics : [];
  const drum = pitch === null ? null : drumOf(context.kit, pitch.drum);
  // A kit is written in two voices by convention -- hands up, feet down -- so a drum whose
  // kit states a direction gets it wherever the event left the stem to the engraver.
  const own = isNote && first ? event.stem : "auto";
  const stem = own === "auto" && drum !== null ? drum.stem : own;
  const staff = staffOf(event, context);

  return [
    leaf("voice", context.voice),
    event.kind === "rest" && event.wholeMeasure ? null : leaf("type", event.duration.type),
    ...Array.from({ length: event.duration.dots }, () => el("dot")),
    pitch === null || drum !== null
      ? null
      : accidentalNode(pitch, context.accidentals[index] ?? null),
    timeModification(event),
    stem === "auto" ? null : leaf("stem", stem),
    drum === null ? null : noteheadNode(drum.notehead),
    staff === null ? null : leaf("staff", staff),
    ...beamNodes(beam, first ? context.beams : []),
    notationsNode(
      pitch,
      context.slurs,
      context.glissandos,
      context.tuplet,
      marksFor(event, index, count),
      drum?.open ?? false,
    ),
    ...lyricNodes(lyrics),
  ];
}

/**
 * A hand-placed offset as the attributes MusicXML carries it in.
 *
 * `relative-x` and `relative-y` are the format's own word for "this far from wherever you
 * were going to put it", which is exactly what the document means; `default-x` and
 * `default-y`, which look similar, are an absolute position an engraver already worked out
 * and are deliberately not written. Both are in tenths and count y upwards, as the document
 * does, so the numbers go straight through.
 */
function placement(offset: Offset | null): Readonly<Record<string, number | null>> {
  if (offset === null) return {};
  return {
    "relative-x": offset.x === 0 ? null : offset.x,
    "relative-y": offset.y === 0 ? null : offset.y,
  };
}

/**
 * The `<note>` elements for one event: one per pitch of a chord, or a single rest.
 *
 * A grace note is written without a `<duration>`, which is the format's way of saying it
 * steals its time from the note after it rather than occupying any of the bar itself.
 */
export function noteNodes(event: MusicEvent, context: NoteContext): readonly XmlNode[] {
  const divisions = toDivisions(ticksOf(event.duration), context.divisions);

  if (event.kind === "rest") {
    const rest = event.wholeMeasure ? el("rest", { measure: "yes" }) : el("rest");
    const lasts = event.wholeMeasure
      ? toDivisions(context.measureTicks, context.divisions)
      : divisions;
    return [
      el("note", { id: context.id, ...placement(event.offset) }, [
        event.cue ? el("cue") : null,
        rest,
        leaf("duration", lasts),
        ...tail(event, context, null, 0, 1),
      ]),
    ];
  }

  return event.pitches.map((pitch, index) => {
    const grace = event.grace;
    const drum = drumOf(context.kit, pitch.drum);
    const head: (XmlNode | null)[] = [
      grace === null ? null : el("grace", grace === "acciaccatura" ? { slash: "yes" } : {}),
      event.cue ? el("cue") : null,
      index === 0 ? null : el("chord"),
      drum === null
        ? el("pitch", {}, [
            leaf("step", pitch.step),
            pitch.alter === 0 ? null : leaf("alter", pitch.alter),
            leaf("octave", pitch.octave),
          ])
        : unpitchedNode(pitch),
      grace === null ? leaf("duration", divisions) : null,
      // A cue is not sounded, and the format's own content model has no `<tie>` on one. The
      // tie is still drawn, because `<notations><tied>` says the line and this says the
      // sound, and a quoted passage keeps the line it was quoted with.
      !event.cue && pitch.tieStop ? el("tie", { type: "stop" }) : null,
      !event.cue && pitch.tieStart ? el("tie", { type: "start" }) : null,
      instrumentNode(context, drum),
    ];
    const id = context.id === null ? null : index === 0 ? context.id : `${context.id}.${index}`;
    return el("note", { id, ...placement(event.offset) }, [
      ...head,
      ...tail(event, context, pitch, index, event.pitches.length),
    ]);
  });
}
